/* Design and extras resolve to the existing Shopify variant; prices come from that variant. */
(() => {
  function optionValues(option) {
    try {
      const values = JSON.parse(option?.dataset.options || '[]');
      return Array.isArray(values) ? values.map(String) : [];
    } catch {
      return [];
    }
  }

  function findVariant(options, selectedValues, optionIndex, nextValue) {
    if (!Number.isInteger(optionIndex) || optionIndex < 0 || optionIndex >= selectedValues.length) return null;
    const requested = [...selectedValues];
    requested[optionIndex] = nextValue;
    return Array.from(options).find((option) => {
      const values = optionValues(option);
      return !option.disabled && values.length === requested.length &&
        values.every((value, index) => value === requested[index]);
    }) || null;
  }

  function services(value) {
    const text = String(value || '').toLocaleLowerCase('de-DE');
    return { check: text.includes('datencheck') && !text.includes('ohne datencheck'), express: text.includes('express') && !text.includes('ohne express') };
  }

  class MoneyHolderDesignPicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.variantSelect = this.form?.querySelector('[data-variant-select]');
      this.section = this.closest('[data-product-section]');
      this.buttons = Array.from(this.querySelectorAll('[data-money-holder-design]'));
      this.extras = Array.from(this.form?.querySelectorAll('[data-money-holder-extra-option]') || []);
      this.serviceButtons = Array.from(this.form?.querySelectorAll('[data-money-holder-service]') || []);
      this.servicesIndex = Number(this.dataset.servicesOptionIndex);
      this.servicesWrapper = this.form?.querySelector('[data-money-holder-services]');
      this.serviceStatus = this.form?.querySelector('[data-money-holder-service-status]');
      this.status = this.querySelector('[data-money-holder-design-status]');
      this.designIndex = Number(this.dataset.designOptionIndex);
      const values = optionValues(this.variantSelect?.selectedOptions[0]);
      if (!this.variantSelect || this.buttons.length < 2 || !this.section ||
          !Number.isInteger(this.designIndex) || this.designIndex < 0 || this.designIndex >= values.length) return;

      this.handleDesign = (event) => this.choose(this.designIndex, event.currentTarget.dataset.moneyHolderDesign);
      this.handleExtra = (event) => this.choose(Number(event.currentTarget.dataset.moneyHolderExtraOption), event.currentTarget.value);
      this.handleService = (event) => this.chooseService(event.currentTarget.dataset.moneyHolderService);
      this.sync = this.sync.bind(this);
      this.buttons.forEach((button) => button.addEventListener('click', this.handleDesign));
      this.extras.forEach((extra) => extra.addEventListener('change', this.handleExtra));
      this.serviceButtons.forEach((button) => button.addEventListener('click', this.handleService));
      this.variantSelect.addEventListener('change', this.sync);
      this.sync();
      this.hidden = false;
      this.section.classList.add('money-holder-designs-ready');
      const extras = this.form.querySelector('[data-money-holder-extra-options]');
      if (extras) extras.hidden = this.extras.length === 0;
      if (this.servicesWrapper) this.servicesWrapper.hidden = this.serviceButtons.length === 0;
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => button.removeEventListener('click', this.handleDesign));
      this.extras?.forEach((extra) => extra.removeEventListener('change', this.handleExtra));
      this.serviceButtons?.forEach((button) => button.removeEventListener('click', this.handleService));
      this.variantSelect?.removeEventListener('change', this.sync);
      this.section?.classList.remove('money-holder-designs-ready');
      const extras = this.form?.querySelector('[data-money-holder-extra-options]');
      if (extras) extras.hidden = true;
      if (this.servicesWrapper) this.servicesWrapper.hidden = true;
      this.hidden = true;
    }

    choose(index, value) {
      const values = optionValues(this.variantSelect.selectedOptions[0]);
      const target = findVariant(this.variantSelect.options, values, index, value);
      if (!target) {
        if (this.status) this.status.textContent = 'Diese Kombination ist zurzeit nicht verfügbar. Bitte prüfe deine Zusatzoptionen.';
        this.sync();
        return;
      }
      if (this.status) this.status.textContent = index === this.designIndex ? `Ausgewählt: ${value}` : '';
      this.variantSelect.value = target.value;
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    serviceVariant(service, enabled) {
      if (!['check', 'express'].includes(service)) return null;
      const values = optionValues(this.variantSelect.selectedOptions[0]);
      if (!Number.isInteger(this.servicesIndex) || this.servicesIndex < 0 || this.servicesIndex >= values.length) return null;
      const requested = { ...services(values[this.servicesIndex]), [service]: enabled };
      return Array.from(this.variantSelect.options).find((option) => {
        const candidate = optionValues(option);
        const state = services(candidate[this.servicesIndex]);
        return !option.disabled && candidate.length === values.length &&
          state.check === requested.check && state.express === requested.express &&
          candidate.every((value, index) => index === this.servicesIndex || value === values[index]);
      }) || null;
    }

    chooseService(service) {
      const values = optionValues(this.variantSelect.selectedOptions[0]);
      const target = this.serviceVariant(service, !services(values[this.servicesIndex])[service]);
      if (!target) {
        if (this.serviceStatus) this.serviceStatus.textContent = 'Diese Zusatzleistung ist für deine Auswahl zurzeit nicht verfügbar.';
        this.sync();
        return;
      }
      if (this.serviceStatus) this.serviceStatus.textContent = '';
      this.choose(this.servicesIndex, optionValues(target)[this.servicesIndex]);
    }

    sync() {
      const values = optionValues(this.variantSelect.selectedOptions[0]);
      this.buttons.forEach((button) => {
        const selected = values[this.designIndex] === button.dataset.moneyHolderDesign;
        const available = Boolean(findVariant(this.variantSelect.options, values, this.designIndex, button.dataset.moneyHolderDesign));
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !available;
        const state = button.querySelector('[data-design-card-state]');
        if (state) state.textContent = selected ? '✓ Ausgewählt' : available ? 'Auswählen' : 'Nicht verfügbar';
      });
      this.extras.forEach((extra) => {
        const index = Number(extra.dataset.moneyHolderExtraOption);
        extra.value = values[index];
        const baseline = findVariant(this.variantSelect.options, values, index, extra.options[0]?.value);
        Array.from(extra.options).forEach((option) => {
          const target = findVariant(this.variantSelect.options, values, index, option.value);
          option.disabled = !target;
          const surcharge = Number(target?.dataset.priceCents) - Number(baseline?.dataset.priceCents);
          const price = Number.isFinite(surcharge) && surcharge > 0 ?
            ` (+${new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(surcharge / 100)})` : '';
          option.textContent = option.value + price;
        });
      });
      const selectedServices = services(values[this.servicesIndex]);
      this.serviceButtons?.forEach((button) => {
        const service = button.dataset.moneyHolderService;
        const selected = selectedServices[service];
        const off = this.serviceVariant(service, false);
        const on = this.serviceVariant(service, true);
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
        button.disabled = !this.serviceVariant(service, !selected);
        const surcharge = Number(on?.dataset.priceCents) - Number(off?.dataset.priceCents);
        const price = button.querySelector('[data-money-holder-service-price]');
        if (price) price.textContent = Number.isFinite(surcharge) && surcharge >= 0 ?
          `+${new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(surcharge / 100)}` : 'Nicht verfügbar';
      });
    }
  }

  if (!customElements.get('money-holder-design-picker')) {
    customElements.define('money-holder-design-picker', MoneyHolderDesignPicker);
  }
})();
